import { PlayerAdvertisement } from "./player_advertisement";
import { PlayerAdvertisementParams } from "./player_advertisement_params";
import { PlayerAhoyEvent } from "./player_ahoy_event";
import { PlayerAhoyCommonEvent } from "./player_ahoy_common_event";
import { PlayerAhoyLivestreamEvent } from "./player_ahoy_livestream_event";
import { PlayerAutoplay } from "./player_autoplay";
import { PlayerFocus } from "./player_focus";
import { PlayerData } from "./player_data";
import { PlayerTech } from "./player_tech";
import { PlayerBrowserSupport } from "./player_browser_support";
import { PlayerEnvironment } from "./player_environment";
import { PlayerError } from "./player_error";
import Ajax from './ajax';
import UUID from "./uuid";
import PlayerOptions from './player_options';
import PlayerAfterSetup from './player_after_setup';

import { Timing } from "@javascripts/utils/timing";
import VidioToken from "@javascripts/utils/vidio_token";

import Blocker from "@javascripts/service/blocker/index";
import Geoblock from '@javascripts/service/geoblock/index';

import ElementAdapter from "@javascripts/adapters/element_adapter";
import LocationAdapter from "@javascripts/adapters/location_adapter";

import NullEvent from "@javascripts/events/null_event";
import VideoEvent from "@javascripts/events/video_event";
import VideoGtmEvent from "@javascripts/events/video_gtm_event";
import LivestreamEvent from "@javascripts/events/livestream_event";
import LivestreamGtmEvent from "@javascripts/events/livestream_gtm_event";

import NullPlenty from "@javascripts/plenty/null_plenty";
import VideoPlenty from "@javascripts/plenty/video_plenty";
import LivestreamPlenty from "@javascripts/plenty/livestream_plenty";
import VideoDom from '@javascripts/components/video_dom';

import "@javascripts/plugins";
import "@javascripts/components";
import TopicAdapter from "@javascripts/adapters/topic_adapter";

import QueryParam from './query_param';
import PerformanceAdapter from "@javascripts/adapters/performance_adapter";
import { PlayerAhoyUtil } from '@javascripts/utils/player_ahoy_util';

export class Player {
  static create(playerDataId, adsEnabled){
    var player;

    player = new this(playerDataId, adsEnabled);
    player.initialize();

    window.player = player;
    return player;
  }

  static createAndReplace(placeholderElement){
    var placeholderContent = placeholderElement.querySelector(".player-placeholder-content");
    var dataPlayerId = placeholderElement.getAttribute("data-player-data-id");

    Player.bindClickTargetEl(dataPlayerId, placeholderContent);
  }

  static bindClickTargetEl(playerDataId, targetEl){
    targetEl.onclick = function(){
      Player.disposeCurrentPlayer();
      Player.setupNewPlayer(playerDataId, targetEl);
    };
  }

  static disposeCurrentPlayer(){
    if(typeof window.player !== "undefined" && window.player instanceof Player){
      window.player.dispose();
      delete window.player;
    }
  }

  static setupNewPlayer(playerDataId, targetEl){
    targetEl.style.display ="none";

    var player = new Player(playerDataId);
    player.initialize();

    window.player = player;

    this.bindOnDispose(targetEl);
  }

  static bindOnDispose(targetEl) {
    var interval = setInterval(
      () => {
        try {
          player.tech().on("dispose", function() {
            targetEl.style.display = "block";
          }.bind(this));
          clearInterval(interval);
        } catch(e) {}
      }
    , 50);
  }

  constructor(playerDataId, adsEnabled){
    this._started = false;
    this._uuid = UUID.v4();
    this._data = new PlayerData(playerDataId, adsEnabled, this.uuid());

    this.removeVjsClipDashUrlOnDemand();

    PlayerAhoyUtil.setAhoyEventGlobalProperties(this._data);
    PlayerAhoyCommonEvent.sendPlatformSupportEvent({
      type: this.data().type(),
      play_uuid: this.uuid()
    });

    this._autoplay = new PlayerAutoplay({ autoplay: this.data().autoplay(), adult: this.data().adult() });
    this._options = new PlayerOptions({
      sources: [{
        src: this.data().hlsUrlWithEmbedParam(),
        type: "application/x-mpegURL"
      }],
      autoplay: this.autoplay(),
      data: this.data(),
      uuid: this.uuid()
    });

    this._tech = new PlayerTech();
    this._blocker = new Blocker({ data: this.data(), tech: this.tech(), uuid: this.uuid() });
    this._error = new PlayerError({ data: this.data(), tech: this.tech(), blocker: this.blocker() });
    this._geoblock = new Geoblock({ data: this.data(), blocker: this.blocker(), uuid: this.uuid(), tech: this.tech() });
    this._adsParams = new PlayerAdvertisementParams(this.data(), this.options().muted);
    this._ads = new PlayerAdvertisement(this.tech(), this.data(), this.adsParams());
    this._event = new NullEvent();
    this._gtmEvent = new NullEvent();
    this._plenty = new NullPlenty();
    this._videoDom = new VideoDom({ data: this.data(), autoplay: this.autoplay() });
    this._autofocus = new PlayerFocus({idleTime: 2000, data: this.data()});

    if (this.data().type() === "Video") {
      this._event = new VideoEvent({ tech: this.tech(), data: this.data(), uuid: this.uuid(), autoplay: this.autoplay(), ads: this.ads(), error: this.error() });
      this._plenty = new VideoPlenty({ tech: this.tech(), data: this.data() });
      if(window.dataLayer){
        this._gtmEvent = new VideoGtmEvent({ tech: this.tech(), data: this.data() });
      }
    }

    if (this.data().type() === "Livestreaming") {
      this._event = new LivestreamEvent({ data: this.data(), tech: this.tech(), ads: this.ads(), uuid: this.uuid() });
      this._plenty = new LivestreamPlenty({ tech: this.tech(), data: this.data() });
      if(window.dataLayer){
        this._gtmEvent = new LivestreamGtmEvent({ tech: this.tech(), data: this.data() });
      }
    }

    this._playerAfterSetup = new PlayerAfterSetup({
      tech: this.tech(),
      data: this.data(),
      ads: this.ads(),
      autoplay: this.autoplay(),
      error: this.error(),
      blocker: this.blocker(),
    });
  }

  playerAfterSetup() {
    return this._playerAfterSetup;
  }

  removeVjsClipDashUrlOnDemand() {
    if (!!this.data().vjsClipDashUrl() && !this.data().drmEnabled() && PlayerEnvironment.isForcedToUseHLS()) {
      this.data().el().removeAttribute('data-vjs-clip-dash-url')
    }
  }

  dispose() {
    if (this.tech().el().querySelector('video') !== null) {
      const parentNode = this.parentNode();
      this.tech().dispose();

      const playerDom = document.createElement("div");
      playerDom.id = this.data().playerId();

      parentNode.appendChild(playerDom);
    } else {
      LocationAdapter.reload(true);
    }
  }

  parentNode(){
    var currentEl = document.querySelector("#" + this.data().playerId());
    if(currentEl !== null){
      return currentEl.parentNode;
    }
}

  initialize(){
    if(PlayerBrowserSupport.isSupported()) {
      this.increaseResourceTimingBufferSize();
      this.bindCheckGeoblock();
      this.loadPlayer();
    } else {
      // error code 8
      this.blocker().renderBlocker('not_supported_browser');
    }
  }

  bindCheckGeoblock(){
    TopicAdapter.subscribe('check_geoblock', () => this.checkGeoblock());
  }

  checkGeoblock(){
    if(this.data().geoblock() == 'true'){
      this.geoblock().runAndBlock().then(blocked => {
        if(blocked){
          this.dispose();
          this.geoblock().showBlocker();
        }
      })
    }
  }

  loadPlayer() {
    Timing.start('vjs-setup');
    this.load();
  }

  data(){
    return this._data;
  }

  error(){
    return this._error;
  }

  geoblock() {
    return this._geoblock;
  }

  blocker(){
    return this._blocker;
  }

  adsParams() {
    return this._adsParams;
  }

  ads(){
    return this._ads;
  }

  autoplay(){
    return this._autoplay;
  }

  event(){
    return this._event;
  }

  gtmEvent(){
    return this._gtmEvent;
  }

  plenty(){
    return this._plenty;
  }

  videoDom() {
    return this._videoDom;
  }

  bindPlaybackEvent(){
    this.bindPauseSetupTiming();
    this.bindResumeSetupTiming();

    this.error().bindError();
    this.event().bindEvents();
    this.plenty().bindEvents();
    this.gtmEvent().bindEvents();

    this.bindAdOnIpadSelective();

    this.bindPosterImage();
    this.bindSeekOnLivestreamStuck();

    this.bindMuted();
    this.bindRestart();
    this.bindCleanRestart();
  }

  bindMuted(){
    this.tech().on("ready", function(){
      if (this.options().muted) {
        this.tech().muted(true);
      }
    }.bind(this));
  }

  bindRestart(){
    this.tech().on("vp-restart", function(){
      this.restart();
    }.bind(this));
  }

  bindCleanRestart() {
    this.tech().on('vp-clean-restart', () => {
      this.cleanRestart();
    });
  }

  restart(){
    var currentTime, newPlayer, techChecker, isPaused;
    currentTime = this.tech().currentTime();
    isPaused = this.tech().paused();
    this.dispose();
    newPlayer = Player.create(this.data().id(), false);

    techChecker = setInterval(function() {
      if(newPlayer.tech() && newPlayer.tech().plugin()) {
        clearInterval(techChecker);
        newPlayer.bindSeekOnRetry(currentTime, isPaused);
      }
    }, 200);
  }

  cleanRestart() {
    this.dispose();
    Player.create(this.data().id(), false);
  }

  bindSeekOnRetry(lastCurrentTime, isPaused){
    this.tech().one("playing", function() {
      this.seekOnVod(lastCurrentTime);
      setTimeout(function(){
        if (isPaused) this.tech().pause();
      }.bind(this), 100);
    }.bind(this));
  }

  seekOnVod(lastCurrentTime){
    if(this.data().isVideo()){
      var checkCurrentTime = setInterval(function(){
        if(this.tech().currentTime() > 0.2){
          clearInterval(checkCurrentTime);
          this.tech().currentTime(lastCurrentTime);
        }
      }.bind(this), 100);
    }
  }

  bindSeekOnLivestreamStuck(){
    this.tech().one("vp-livestream-stuck", function(data){
      this.tech().currentTime(data.time);
    }.bind(this));
  }

  tech(){
    return this._tech;
  }

  bindPauseSetupTiming(){
    this.tech().on("ready", function(){
      if (!this.autoplay().value()){
        Timing.pause("vjs-setup");
      }
    }.bind(this));
  }

  bindResumeSetupTiming(){
    this.tech().one("firstplay", function(){
      if(!this.autoplay().value()){
        Timing.resume("vjs-setup");
      }
    }.bind(this));
  }

  displayAdOverlay(){
    var playerEl = document.getElementById(this.data().playerId());
    playerEl.insertBefore(ElementAdapter.parseHTML(this.renderAdsOverlay()), playerEl.firstChild);

    var adsOverlayEl = document.getElementsByClassName("vjs-ads__overlay")[0];
    adsOverlayEl.style.display = "block";
  }

  callbackAdSkippableChange(){
    if(this.ads().imaAdSkippable()){
      var el = document.getElementsByClassName("vjs-ads__skip-button")[0];
      if(el !== undefined){
        el.style.display = "block";
      }
      this.bindAdSkipButton();
    }
  }

  bindAdSkipButton(){
    var el = document.getElementsByClassName("vjs-ads__skip-button")[0];
    el.addEventListener("touchend", function(){
      this.ads().skip();
    }.bind(this));
  }

  bindAdSkippableChange(){
    this.ads().ima().addEventListener(google.ima.AdEvent.Type.SKIPPABLE_STATE_CHANGED, this.callbackAdSkippableChange.bind(this));
  }

  bindAdOnIpadSelective(){
    if (PlayerEnvironment.osVersion().indexOf("9.3") == 0 && PlayerEnvironment.deviceModel() == 'iPad' && PlayerEnvironment.browserName() == "Mobile Safari") {
      this.bindAdOnIpad();
    }
  }

  bindAdOnIpad(){
    this.tech().one("adstart", function(){
      this.displayAdOverlay();
      this.bindAdSkippableChange();
      this.bindAdToggleOverlay();
      this.bindAdEndOnIpad();
    }.bind(this));
  }

  bindAdToggleOverlay(){
    var el = document.getElementsByClassName("vjs-ads__overlay")[0];
    el.addEventListener("touchend", function(){
      if(this.tech().paused()){
        this.tech().play();
      } else {
        this.tech().pause();
        this.sendPauseUserTriggeredEvent();
      }
    }.bind(this));
  }

  sendPauseUserTriggeredEvent() {
    this.tech().trigger({
      type: "vp-pause",
      userTriggered: true
    });
  }

  bindAdEndOnIpad(){
    this.tech().on("adend", function(){
      var el = window.document.getElementsByClassName("vjs-ads__overlay")[0];
      el.parentNode.removeChild(el);
    });
  }

  initAndBindAds(){
    this.ads().initPlayerAdvertisements();
    this.ads().initPauseAdvertisement();
    this.bindAdOverlayEvent();
    this.ads().bindStartSqueezeFrameAd();
    this.ads().bindTickerTapeAd()
    this.ads().bindSuperimposeAd()
    this.ads().setupLottieLoader()
    this.ads().setupAdsTransition()
  }

  isSupportVP9() {
    return this.data().vjsClipDashUrl() && PlayerEnvironment.isSupportVp9()
  }

  load(){
    this.replacePlayer();
    this.bindAdsRefresh();

    if (this.data().drmEnabled() && this.data().isLivestreaming()){
      this.getLsDrmSources();
      return
    }
    if (this.data().drmEnabled() && !this.data().isLivestreaming()){
      this.getDrmSources();
      return
    }
    if (this.isSupportVP9() && this.data().isLivestreaming() && PlayerEnvironment.isForcedToUseHLS()){
      this.getSourcesToken();
      return
    }
    if (this.isSupportVP9() && this.data().isLivestreaming() && !PlayerEnvironment.isForcedToUseHLS()){
      this.getSourceLivestreamingVp9();
      return
    }
    if (this.isSupportVP9() && !this.data().isLivestreaming()){
      this.getSourceVp9();
      return
    }
    if (this.data().videoWithToken()){
      this.getSourcesToken();
      return
    }

    this.getAdvertisementParams();
  }

  bindAdsRefresh(){
    if(PlayerEnvironment.browserName() == 'Chrome') {
      var adContainer;
      var x = setInterval(function() {
        adContainer = document.getElementById('ima-ad-container');
        if(!this.isAdPlaying() && adContainer){
          adContainer.addEventListener('click', function() {
            const playPromise = this.tech().play();

            if (playPromise !== undefined) {
              playPromise.then(() => {
                this.tech().pause();
              });
            }
          }.bind(this));
          clearInterval(x)
        }
      }.bind(this), 500);
    }
  }

  isAdPlaying() {
    const playerTech = this.tech()
    if(typeof(playerTech) != 'function' && typeof(playerTech.plugin()) != 'function'){
      if(playerTech.plugin() != undefined && typeof(playerTech.plugin().instance().ads) != 'function'){
        return playerTech.plugin().instance().ads.isAdPlaying();
      } else {
        return false;
      }
    }
  }

  replacePlayer(){
    var parentNode = this.parentNode();
    if(typeof parentNode !== "undefined"){
      parentNode.innerHTML = this.videoDom().render();
    }
  }

  renderAdsOverlay(){
    var vjs = this.tech().plugin().instance();
    var height = vjs.el().offsetHeight - vjs.controlBar.el().offsetHeight;
    return `<div style="height: ${height}px;" class="vjs-ads__overlay vjs-ads-ima__overlay">
      <div class="vjs-ads__skip-button vjs-ads-ima__skip-button">
    </div>`;
  }

  techOrder(){
    return ["html5"];
  }

  getSourceLivestreamingVp9() {
    VidioToken.postToken("/live/"+this.data().videoId()+"/tokens?type=dash").then(function(token){
      var location = document.createElement("a");
      location.href = this.data().vjsClipDashUrl();
      this._options._sources = [{
        src: location.origin + "/" + token + location.pathname.replace(/(\/)?/, '/'),
        type: "application/dash+xml"
      }];

      this.getAdvertisementParams();
    }.bind(this));
  }

  getSourceVp9() {
    this._options._sources = [{
      src: this.data().vjsClipDashUrl(),
      type: "application/dash+xml"
    }];
    this.getAdvertisementParams();
  }

  getDrmSources() {
    this.getDrmSourcesWithInterval(`/drm/v1/cdata?video_id=${this.data().videoId()}`);
  }

  getLsDrmSources() {
    this.getDrmSourcesWithInterval(`/drm/v1/cdata?live_id=${this.data().videoId()}`);
  }

  getDrmSourcesWithInterval(url) {
    let counter = 0;

    if (QueryParam.headerEnrichment() && !window.watchHe) {
      const token_check_interval = window.setInterval(() => {
        if (window.watchHe || counter > 10) {
          window.clearInterval(token_check_interval);

          this.ajaxGetDrmSource(url);
        }

        counter++;
      }, 200);
    } else {
      this.ajaxGetDrmSource(url);
    }
  }

  ajaxGetDrmSource(url) {
    const headers = window.watchHe ? { 'X-Watch-He' : window.watchHe } : {};

    Ajax.get({
      url: url,
      headers: headers,
      success: this.getDrmSourcesOnSuccess.bind(this),
      error: this.getDrmSourcesOnError.bind(this)
    });
  }

  getDrmSourcesOnSuccess(response) {
    response = JSON.parse(response);

    var siteId = this.data().siteId();
    let drmLicenseUrl, fairplayCertificateUrl;
    drmLicenseUrl = 'https://license-global.pallycon.com/ri/licenseManager.do';
    fairplayCertificateUrl = 'https://license.pallycon.com/ri/fpsKeyManager.do?siteId='+ siteId;

    if (response.drm_license_url != undefined) {
      drmLicenseUrl = response.drm_license_url;
    }

    if (response.fairplay_certificate_url != undefined) {
      fairplayCertificateUrl = response.fairplay_certificate_url
    }

    if (PlayerEnvironment.isBrowserSafari() || PlayerEnvironment.isOSiOS()) {
      this.getDrmHlsSource(response.fairplay, drmLicenseUrl, fairplayCertificateUrl);
    } else {
      this.getDrmDashSource(response.widevine, response.playready, drmLicenseUrl);
    }
  }

  showSignInBlocker() {
    this.blocker().showSignInBlocker();
  }

  getDrmSourcesOnError(response, xhr) {
    if (xhr.status() === 401) {
      this.tech().init(this.data().playerId());
      this.tech().el().addClass('video-placeholder');
      this.showSignInBlocker();
    }
  }

  getDrmDashSource(customDataWidevine, customDataPlayready, drmLicenseUrl){
    var vjsUrl = this.data().vjsClipDashUrl();
    this._options._sources = [
      {
        src: vjsUrl,
        type: "application/dash+xml",
        keySystemOptions: [
          {
            name: 'com.widevine.alpha',
            options:{
              serverURL : drmLicenseUrl,
              httpRequestHeaders : {
                'pallycon-customdata-v2': customDataWidevine
              }
            }
          },
          {
            name: 'com.microsoft.playready',
            options:{
              serverURL : drmLicenseUrl,
              httpRequestHeaders : {
                'pallycon-customdata-v2': customDataPlayready
              }
            }
          }
        ]
      },
    ];
    this.getAdvertisementParams();
  }

  getDrmHlsSource(customData, drmLicenseUrl, fairplayCertificateUrl){
    this.getAdvertisementParams(this.getFairplayLicenseOnReady.bind(this, customData, drmLicenseUrl, fairplayCertificateUrl));
  }

  getFairplayLicenseOnReady(customData, drmLicenseUrl, fairplayCertificateUrl) {
    var vjsUrl = this.data().hlsUrlWithEmbedParam();

    this.tech().on('ready', () => {
      pallyCon.fairplay({
        license_url : drmLicenseUrl,
        player: this.tech().plugin().instance(),
        pallycon_custom_data : customData,
        certificate_uri: fairplayCertificateUrl,
        content_url: vjsUrl
      });
    });
  }

  getSourcesToken(){
    var vjsUrl = this.data().hlsUrlWithEmbedParam();
    VidioToken.postToken("/live/"+this.data().videoId()+"/tokens").then(function(token){
      this._options._sources = [{
        src: vjsUrl + "?" + token,
        type: "application/x-mpegURL"
      }];
      this.getAdvertisementParams();
    }.bind(this));
  }

  options(){
    return this._options.value();
  }

  getAdvertisementParams(callback = () => {}) {
    this.adsParams().getVideoAdsParamsViaApi(() => {
      this.setup();
      callback();
    });
  }

  setup(){
    try {
      this.tech().init(this.data().playerId(), this.options());
      this.bindPlaybackEvent();
    } catch(e){
      console.error(e);
      this.sendSetupError(e.message);
      return;
    }

    this.playerAfterSetup().init();
    this.setupComponent();
  }

  showWatermark() {
    if (this.playerAfterSetup().userIdWatermark()) {
      this.playerAfterSetup().userIdWatermark().showWatermark();
    }
  }

  hideWatermark() {
    if (this.playerAfterSetup().userIdWatermark()) {
      this.playerAfterSetup().userIdWatermark().hideWatermark();
    }
  }

  toggleWatermark(duration) {
    if (this.playerAfterSetup().userIdWatermark()) {
      this.playerAfterSetup().userIdWatermark().toggleWatermark(duration * 1000);
    }
  }

  setupComponent(){
    this.initAndBindAds();
  }

  sendSetupError(message){
    if (this.data().type() === "Video") {
      PlayerAhoyEvent.sendSetupError(this.data().videoId(), message, this.ads().paramsPresent(), this.uuid(), this.data().tokenRefreshCount());
    }
    else if (this.data().type() === "Livestreaming") {
      PlayerAhoyLivestreamEvent.sendSetupError(this.data().videoId(), message, this.ads().paramsPresent(), this.uuid());
    }
  }

  uuid(){
    return this._uuid;
  }

  videoElement() {
    return this.tech().videoElement();
  }

  bindPosterImage(){
    this.tech().on("ready", function(){
      !this.autoplay().value() && this.tech().poster(this.data().videoImageUrl());
    }.bind(this));
  }

  bindAdOverlayEvent(){
    if(PlayerEnvironment.isDesktopSite() || PlayerEnvironment.isMobileSite()){
      this.ads().bindStartAdOverlay();
    }
  }

  env() {
    return PlayerEnvironment;
  }

  isReady() {
    if (this.tech() &&
      this.tech().plugin() &&
      this.tech().plugin().instance() &&
      this.tech().el() &&
      this.tech().el().querySelector('video') !== null) {
      return this.tech().plugin().instance().isReady_;
    } else {
      return false;
    }
  }

  increaseResourceTimingBufferSize() {
    if (PlayerEnvironment.isBrowserSafari() && this.data().isLivestreaming()) {
      PerformanceAdapter.setResourceTimingBufferSize(Number.MAX_SAFE_INTEGER);
    }
  }
}
